import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { organizationsAPI, adminAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { isOrgAdminRole, isSaasAdminRole } from '../utils/iamRoles';

const OrganizationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { confirm } = useConfirmDialog();
  const { user } = useAuth();
  const role = String(user?.role || '');
  const currentOrgId = String(user?.organization_id || localStorage.getItem('organization_id') || '');
  const isOrgAdmin = isOrgAdminRole(role);
  const isSaasAdmin = isSaasAdminRole(role);
  const canDeleteOrganization = isSaasAdmin;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'modules', 'apps', 'projects', 'billing'
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [billingSummary, setBillingSummary] = useState({
    subscription: null,
    invoices: [],
    transactions: [],
  });
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [renamingProject, setRenamingProject] = useState(null);
  const [projectRenameValue, setProjectRenameValue] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    type: 'autre',
    email: '',
    phone: '',
    address: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    // Garde-fou UI : un org_admin ne peut consulter que sa propre organisation.
    if (isOrgAdmin && !isSaasAdmin && currentOrgId && id && String(id) !== currentOrgId) {
      showError('Accès restreint: redirection vers votre organisation.');
      navigate(`/saas/organizations/${currentOrgId}`, { replace: true });
      return;
    }
    loadOrganization();
  }, [id]);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      const response = await organizationsAPI.getById(id);
      const org = response.data;
      setFormData({
        name: org.name || '',
        subdomain: org.subdomain || '',
        type: org.type || 'autre',
        email: org.email || '',
        phone: org.phone || '',
        address: org.address || '',
        description: org.description || '',
        is_active: org.is_active !== undefined ? org.is_active : true
      });
      
      // Charger les modules par défaut
      loadModules();
    } catch (err) {
      showError('Erreur lors du chargement de l\'organisation');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await organizationsAPI.update(id, formData);
      success('Organisation mise à jour avec succès');
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const loadModules = async () => {
    try {
      setLoadingModules(true);
      const response = await organizationsAPI.getModules(id);
      setModules(response.data || []);
    } catch (err) {
      showError('Erreur lors du chargement des modules');
      console.error(err);
    } finally {
      setLoadingModules(false);
    }
  };

  const handleToggleModule = async (moduleId, currentEnabled) => {
    try {
      await organizationsAPI.updateModule(id, moduleId, { enabled: !currentEnabled });
      success(`Module ${!currentEnabled ? 'activé' : 'désactivé'} avec succès`);
      loadModules();
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de la mise à jour du module');
    }
  };

  const handleToggleComponent = async (moduleId, componentId, currentEnabled) => {
    try {
      await organizationsAPI.updateComponent(id, moduleId, componentId, { enabled: !currentEnabled });
      success(`Composant ${!currentEnabled ? 'activé' : 'désactivé'} avec succès`);
      loadModules();
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de la mise à jour du composant');
    }
  };

  const loadApps = async () => {
    try {
      setLoadingApps(true);
      const response = await organizationsAPI.getOrganizationApps(id);
      setApps(response.data || []);
    } catch (err) {
      showError('Erreur lors du chargement des apps');
      console.error(err);
    } finally {
      setLoadingApps(false);
    }
  };

  const loadBillingSummary = async () => {
    try {
      setLoadingBilling(true);
      const [subsRes, invRes, txRes] = await Promise.all([
        adminAPI.getSubscriptions({ organization_id: id, limit: 1, skip: 0 }),
        adminAPI.getInvoices({ organization_id: id, limit: 10, skip: 0 }),
        adminAPI.getOrganizationBillingTransactions(id, { limit: 10, skip: 0 }),
      ]);
      const subscriptions = Array.isArray(subsRes?.data?.subscriptions)
        ? subsRes.data.subscriptions
        : [];
      const invoices = Array.isArray(invRes?.data?.invoices) ? invRes.data.invoices : [];
      const transactions = Array.isArray(txRes?.data?.transactions)
        ? txRes.data.transactions
        : [];
      setBillingSummary({
        subscription: subscriptions[0] || null,
        invoices,
        transactions,
      });
    } catch (err) {
      console.error(err);
      showError('Erreur lors du chargement des données de facturation');
    } finally {
      setLoadingBilling(false);
    }
  };

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await adminAPI.getProjects({
        organization_id: id,
        limit: 100,
        skip: 0,
      });
      const rows = Array.isArray(response?.data?.projects) ? response.data.projects : [];
      setProjects(rows);
    } catch (err) {
      console.error(err);
      showError('Erreur lors du chargement des projets');
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleActivateApp = async (appId) => {
    try {
      const response = await organizationsAPI.activateApp(id, appId);
      success(`App activée avec succès. Un projet par défaut a été créé automatiquement.`);
      loadApps();
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de l\'activation de l\'app');
    }
  };

  const handleDeactivateApp = async (appId) => {
    const confirmed = await confirm({
      title: 'Désactiver l\'app',
      message: 'Êtes-vous sûr de vouloir désactiver cette app ? Le projet associé sera marqué comme inactif mais ne sera pas supprimé.',
      type: 'warning'
    });
    
    if (!confirmed) return;

    try {
      await organizationsAPI.deactivateApp(id, appId);
      success('App désactivée avec succès');
      loadApps();
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de la désactivation de l\'app');
    }
  };

  const handleRenameProject = async (projectId, newName) => {
    try {
      await organizationsAPI.updateProject(projectId, { name: newName });
      success('Projet renommé avec succès');
      setRenamingProject(null);
      setProjectRenameValue('');
      loadApps();
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors du renommage du projet');
    }
  };

  const handleDelete = async () => {
    const orgName = formData.name;
    const confirmed = await confirm({
      title: 'Supprimer l\'organisation',
      message: `Êtes-vous sûr de vouloir supprimer définitivement l'organisation "${orgName}" ? Cette action est irréversible et désactivera l'organisation.`,
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await organizationsAPI.delete(id);
      success('Organisation supprimée avec succès');
      navigate('/saas/organizations');
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const organizationTypes = [
    { value: 'association', label: 'Association' },
    { value: 'caisse_entraide', label: 'Caisse d\'entraide' },
    { value: 'mutuelle', label: 'Mutuelle' },
    { value: 'paroisse', label: 'Paroisse' },
    { value: 'mouvement', label: 'Mouvement (scout, jeunesse, femmes, etc.)' },
    { value: 'cooperative', label: 'Coopérative' },
    { value: 'autre', label: 'Autre' }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Organisation : ${formData.name}`}
        description="Gérer les détails et modules de l'organisation"
      />

      {/* Carte d'information */}
      <div className="bg-gradient-to-br from-entrea-panel via-entrea-surface to-entrea-panel rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 bg-gradient-to-br from-entrea-ink to-entrea-royal rounded-xl flex items-center justify-center text-white text-2xl font-bold">
              {formData.name?.charAt(0).toUpperCase() || 'O'}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{formData.name}</h2>
              <p className="text-sm text-gray-600 font-mono">{formData.subdomain}.mobile.uruganda.app</p>
            </div>
          </div>
          <span className={`px-4 py-2 inline-flex text-sm leading-5 font-semibold rounded-full ${
            formData.is_active
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {formData.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Détails
            </button>
            <button
              onClick={() => {
                setActiveTab('modules');
                if (modules.length === 0) loadModules();
              }}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'modules'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Modules
            </button>
            <button
              onClick={() => {
                setActiveTab('apps');
                if (apps.length === 0) loadApps();
              }}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'apps'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Apps & Projets
            </button>
            <button
              onClick={() => {
                setActiveTab('projects');
                loadProjects();
              }}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'projects'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Projets
            </button>
            <button
              onClick={() => {
                setActiveTab('billing');
                loadBillingSummary();
              }}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'billing'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Abonnement & Factures
            </button>
          </nav>
        </div>
      </div>

      {/* Formulaire */}
      {activeTab === 'details' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nom de l'organisation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Type d'organisation <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                required
              >
                {organizationTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sous-domaine <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  name="subdomain"
                  value={formData.subdomain}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                  required
                  readOnly
                />
                <span className="px-4 py-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                  .mobile.uruganda.app
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">Le sous-domaine ne peut pas être modifié</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-entrea-royal border-gray-300 rounded focus:ring-entrea-royal/40"
                />
                <span className="text-sm font-semibold text-gray-700">Organisation active</span>
              </label>
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <div>
              {canDeleteOrganization && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Supprimer
                </button>
              )}
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/saas/organizations')}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </form>
      </div>
      )}

      {/* Gestion des modules */}
      {activeTab === 'modules' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Modules de l'organisation</h3>
            <p className="text-sm text-gray-600 mt-1">
              Activez ou désactivez les modules disponibles pour cette organisation selon ses besoins.
            </p>
          </div>

          {loadingModules ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-entrea-royal mx-auto"></div>
                <p className="mt-2 text-gray-600">Chargement des modules...</p>
              </div>
            </div>
          ) : modules.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucun module disponible</p>
          ) : (
            <div className="space-y-4">
              {modules.map((module) => (
                <div
                  key={module.id}
                  className={`border rounded-lg p-4 ${
                    module.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          module.enabled
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{module.display_name}</h4>
                        {module.description && (
                          <p className="text-sm text-gray-600">{module.description}</p>
                        )}
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={module.enabled}
                        onChange={() => handleToggleModule(module.id, module.enabled)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-entrea-royal/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-entrea-royal"></div>
                    </label>
                  </div>

                  {module.components && module.components.length > 0 && (
                    <div className="mt-3 pl-13 space-y-2">
                      {module.components.map((component) => (
                        <div
                          key={component.id}
                          className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                        >
                          <div>
                            <span className="text-sm font-medium text-gray-700">
                              {component.display_name}
                            </span>
                            {component.description && (
                              <p className="text-xs text-gray-500">{component.description}</p>
                            )}
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={component.enabled}
                              onChange={() =>
                                handleToggleComponent(module.id, component.id, component.enabled)
                              }
                              disabled={!module.enabled}
                              className="sr-only peer"
                            />
                            <div
                              className={`w-9 h-5 rounded-full peer ${
                                !module.enabled
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : 'bg-gray-200 peer-checked:bg-entrea-royal'
                              } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-entrea-royal/30 peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all`}
                            ></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Gestion des Apps et Projets */}
      {activeTab === 'apps' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Apps & Projets</h3>
            <p className="text-sm text-gray-600 mt-1">
              Activez des apps pour cette organisation. Chaque activation crée automatiquement un projet par défaut (non supprimable, mais renommable).
            </p>
          </div>

          {loadingApps ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-entrea-royal mx-auto"></div>
                <p className="mt-2 text-gray-600">Chargement des apps...</p>
              </div>
            </div>
          ) : apps.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune app disponible</p>
          ) : (
            <div className="space-y-4">
              {apps.map((app) => (
                <div
                  key={app.id}
                  className={`border rounded-lg p-5 ${
                    app.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            app.enabled
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-gray-200 text-gray-400'
                          }`}
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-900">{app.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{app.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                              {app.type}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              app.status === 'production' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {app.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      {app.enabled && app.project && (
                        <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-700 mb-1">Projet associé :</div>
                              {renamingProject === app.project.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={projectRenameValue}
                                    onChange={(e) => setProjectRenameValue(e.target.value)}
                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                                    placeholder="Nom du projet"
                                  />
                                  <button
                                    onClick={() => handleRenameProject(app.project.id, projectRenameValue)}
                                    className="px-3 py-1.5 text-sm bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy"
                                  >
                                    Enregistrer
                                  </button>
                                  <button
                                    onClick={() => {
                                      setRenamingProject(null);
                                      setProjectRenameValue('');
                                    }}
                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                                  >
                                    Annuler
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">{app.project.name}</span>
                                  {app.project.is_default && (
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                      Par défaut
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    app.project.status === 'active' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {app.project.status}
                                  </span>
                                  {app.project.is_default && (
                                    <button
                                      onClick={() => {
                                        setRenamingProject(app.project.id);
                                        setProjectRenameValue(app.project.name);
                                      }}
                                      className="text-xs text-entrea-royal hover:text-entrea-ink font-medium"
                                    >
                                      Renommer
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={app.enabled}
                          onChange={() => {
                            if (app.enabled) {
                              handleDeactivateApp(app.id);
                            } else {
                              handleActivateApp(app.id);
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'projects' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Projets de l'organisation</h3>
            <p className="text-sm text-gray-600 mt-1">
              Liste des projets liés à cette organisation.
            </p>
          </div>

          {loadingProjects ? (
            <div className="text-sm text-gray-500">Chargement...</div>
          ) : projects.length === 0 ? (
            <p className="text-sm text-gray-500 py-6">Aucun projet trouvé.</p>
          ) : (
            <div className="space-y-3">
              {projects.map((project) => (
                <div key={project.id || `${project.app_id}-${project.name}`} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{project.name || 'Projet sans nom'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        App: {project.app_id || '-'} · Statut: {project.status || '-'}
                      </p>
                    </div>
                    {project.is_default && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Par défaut
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Abonnement & Facturation</h3>
            <p className="text-sm text-gray-600 mt-1">
              Vue des éléments liés à cette organisation: abonnement actif, factures et transactions.
            </p>
          </div>

          {loadingBilling ? (
            <div className="text-sm text-gray-500">Chargement...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Plan</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {billingSummary.subscription?.plan_name || '-'}
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Statut abonnement</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {billingSummary.subscription?.status || '-'}
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Montant mensuel</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {Number(billingSummary.subscription?.amount || 0).toLocaleString('fr-FR')}{' '}
                    {billingSummary.subscription?.currency || ''}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Factures récentes</h4>
                <div className="space-y-2">
                  {billingSummary.invoices.map((inv) => (
                    <div key={inv.id || inv.invoice_number} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{inv.invoice_number || inv.id}</p>
                        <p className="text-xs text-gray-500">{inv.status || '-'}</p>
                      </div>
                      <div className="text-sm font-semibold text-gray-900">
                        {Number(inv.total_amount || inv.amount || 0).toLocaleString('fr-FR')} {inv.currency || ''}
                      </div>
                    </div>
                  ))}
                  {billingSummary.invoices.length === 0 && (
                    <p className="text-sm text-gray-500 py-2">Aucune facture trouvée.</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Transactions récentes</h4>
                <div className="space-y-2">
                  {billingSummary.transactions.map((tx) => (
                    <div key={tx.id || tx.reference || tx.created_at} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{tx.reference || tx.id || 'Transaction'}</p>
                        <p className="text-xs text-gray-500">{tx.status || '-'} · {tx.created_at || ''}</p>
                      </div>
                      <div className="text-sm font-semibold text-gray-900">
                        {Number(tx.amount || 0).toLocaleString('fr-FR')} {tx.currency || ''}
                      </div>
                    </div>
                  ))}
                  {billingSummary.transactions.length === 0 && (
                    <p className="text-sm text-gray-500 py-2">Aucune transaction trouvée.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationDetails;

